import axios from 'axios'
import apiClient from '../enrollment-platform-client/api-client'
import epsClient from '../eps.client'
import { financeService } from '@hixme/api'
import { isDevelopment, isStg } from 'modules/app-config/environment'
import { STG_ENV } from 'modules/app-config/stg-env'
import type {
  CreateVcnWexRequestDto,
  CreateVcnWexResponseDto,
} from './dto/create-vcn-wex.dto'
import type { ActivateVcnWexResponseDto } from './dto/activate-vcn-wex.dto'
import type { GetVcnWexByBenefitResponseDto } from './dto/get-vcn-wex-by-benefit.dto'
import type { ListVcnWexByEmployeeResponseDto } from './dto/list-vcn-wex-by-employee.dto'
import type { ReuseVcnWexResponseDto } from './dto/reuse-vcn-wex.dto'
import type { SuspendVcnWexResponseDto } from './dto/suspend-vcn-wex.dto'
import type { TerminateVcnWexResponseDto } from './dto/terminate-vcn-wex.dto'
import type { GetWexVcnTransactionsResponseDto } from './dto/get-wex-vcn-transactions.dto'
import type {
  AvailableVcnCard,
  MarqetaFamilyCardDto,
  MarqetaFamilyVcnResponse,
  WexVirtualCardWithDetails,
  ApplicationDto,
} from './dto/available-vcn-card.dto'
import { getPartnerVcnWexShowPan } from 'partner-payment-service/api/vcn'
import { VirtualCardType } from 'modules/virtual-cards/constants'

import type {
  TerminateAllCardsRequestDto,
  TerminateAllCardsResponseDto,
} from './dto/terminate-all-cards.dto'
import type {
  MarqetaVirtualCardResponse,
  PerformMarqetaCardActionParams,
} from './dto/marqeta-vcn.dto'

export type {
  CreateVcnWexRequestDto,
  CreateVcnWexResponseDto,
} from './dto/create-vcn-wex.dto'
export type { ActivateVcnWexResponseDto } from './dto/activate-vcn-wex.dto'
export type {
  GetVcnWexByBenefitResponseDto,
  VcnWexByBenefitItemDto,
} from './dto/get-vcn-wex-by-benefit.dto'
export type { ListVcnWexByEmployeeResponseDto } from './dto/list-vcn-wex-by-employee.dto'
export type { ReuseVcnWexResponseDto } from './dto/reuse-vcn-wex.dto'
export type { SuspendVcnWexResponseDto } from './dto/suspend-vcn-wex.dto'
export type { TerminateVcnWexResponseDto } from './dto/terminate-vcn-wex.dto'
export type {
  VcnWexCardDto,
  VcnWexEmployeeListItemDto,
  VcnWexRawDataDto,
  VcnWexUserDefinedFieldsDto,
  VcnWexVirtualCardNestedDto,
  VcnWexVirtualCardPaymentDto,
} from './dto/vcn-wex.dto'
export type {
  GetWexVcnTransactionsResponseDto,
  WexVcnAuthorizationItemDto,
  WexVcnMergedTransactionItemDto,
  WexVcnTransactionItemDto,
} from './dto/get-wex-vcn-transactions.dto'
export { WexVcnMergedTransactionKinds } from './dto/get-wex-vcn-transactions.dto'
export type {
  AvailableVcnCard,
  MarqetaFamilyCardDto,
  MarqetaFamilyVcnResponse,
  WexVirtualCardWithDetails,
} from './dto/available-vcn-card.dto'
export type {
  TerminateAllCardsRequestDto,
  TerminateAllCardsResponseDto,
  TerminateAllCardResultDto,
  TerminateAllSkippedCardDto,
} from './dto/terminate-all-cards.dto'
export type {
  MarqetaVirtualCardDto,
  MarqetaVirtualCardResponse,
  MarqetaCardAction,
  PerformMarqetaCardActionParams,
} from './dto/marqeta-vcn.dto'

// ============================================================================
// Terminate and Cleanup Cards
// ============================================================================

/**
 * Terminate and cleanup all active virtual cards for a benefit (both Marqeta and WEX).
 * Closes the benefit's own card plus every other card the employee holds
 * that no OTHER active benefit is bound to.
 * `POST /api/v1/vcn-management/cards/terminate-and-cleanup`
 */
export async function terminateAndCleanupCards(
  params: TerminateAllCardsRequestDto
): Promise<TerminateAllCardsResponseDto> {
  const response = await apiClient.post<TerminateAllCardsResponseDto>(
    'vcn-management/cards/terminate-and-cleanup',
    params
  )
  return response.data
}

/**
 * Enrollment Platform WEX VCN (`/api/v1/vcn/...` — paths are relative to EPS `api/v1` base URL).
 */

/** `POST /api/v1/vcn/wex` with `{ benefitId }` body. */
export async function createWexVirtualCard(
  benefitId: string
): Promise<CreateVcnWexResponseDto> {
  const body: CreateVcnWexRequestDto = { benefitId }
  const response = await apiClient.post<CreateVcnWexResponseDto>(
    'vcn/wex',
    body
  )
  return response.data
}

/** `GET /api/v1/vcn/wex?employeeId=...` */
export async function listWexVirtualCardsByEmployeeId(
  employeeId: string
): Promise<ListVcnWexByEmployeeResponseDto> {
  const response = await apiClient.get<ListVcnWexByEmployeeResponseDto>(
    'vcn/wex',
    { params: { employeeId } }
  )
  return response.data
}

/** `GET /api/v1/vcn/wex?benefitId=...` */
export async function getWexVirtualCardsByBenefitId(
  benefitId: string
): Promise<GetVcnWexByBenefitResponseDto> {
  const response = await apiClient.get<GetVcnWexByBenefitResponseDto>(
    'vcn/wex',
    { params: { benefitId } }
  )
  return response.data
}

/** `POST /api/v1/vcn/wex/:merchantLogId/activate` — no request body; same JSON as suspend/terminate/reuse. */
export async function activateWexVirtualCard(
  merchantLogId: string
): Promise<ActivateVcnWexResponseDto> {
  const response = await apiClient.post<ActivateVcnWexResponseDto>(
    `vcn/wex/${encodeURIComponent(merchantLogId)}/activate`
  )
  return response.data
}

/** `POST /api/v1/vcn/wex/:merchantLogId/suspend` — no request body; same JSON as terminate/reuse. */
export async function suspendWexVirtualCard(
  merchantLogId: string
): Promise<SuspendVcnWexResponseDto> {
  const response = await apiClient.post<SuspendVcnWexResponseDto>(
    `vcn/wex/${encodeURIComponent(merchantLogId)}/suspend`
  )
  return response.data
}

/** `POST /api/v1/vcn/wex/:merchantLogId/terminate` — optional `benefitId` query. */
export async function terminateWexVirtualCard(
  merchantLogId: string,
  benefitId?: string
): Promise<TerminateVcnWexResponseDto> {
  const response = await apiClient.post<TerminateVcnWexResponseDto>(
    `vcn/wex/${encodeURIComponent(merchantLogId)}/terminate`,
    undefined,
    { params: benefitId ? { benefitId } : undefined }
  )
  return response.data
}

/** `POST /api/v1/vcn/wex/:merchantLogId/reuse` — no request body. */
export async function reuseWexVirtualCard(
  merchantLogId: string,
  benefitId: string
): Promise<ReuseVcnWexResponseDto> {
  const response = await apiClient.post<ReuseVcnWexResponseDto>(
    `vcn/wex/${encodeURIComponent(merchantLogId)}/reuse`,
    { benefitId }
  )
  return response.data
}

/** `GET /api/v1/vcn/wex/:merchantLogId/transactions` — activity + merged timeline. */
export async function getWexVcnTransactions(
  merchantLogId: string
): Promise<GetWexVcnTransactionsResponseDto> {
  const response = await apiClient.get<GetWexVcnTransactionsResponseDto>(
    `vcn/wex/${encodeURIComponent(merchantLogId)}/transactions`
  )
  return response.data
}

// ============================================================================
// Family Virtual Cards (Marqeta + WEX combined)
// ============================================================================

function remapFamilyVirtualCardCard(card: MarqetaFamilyCardDto) {
  const metadata = card.metadata ?? {}
  return {
    ...card,
    card_public_key: card.cardPublicKey ?? card.card_public_key,
    user_public_key: card.userPublicKey ?? card.user_public_key,
    metadata: {
      ...metadata,
      HiosId: metadata.hiosId ?? metadata.HiosId,
    },
  }
}

/**
 * Fetch all virtual cards for an employee (both Marqeta and WEX).
 * `GET /core/finances/family/:employeePublicKey/virtual-card` for Marqeta
 * `GET /api/v1/vcn/wex?employeeId=...` for WEX
 */
export async function getVirtualCardsForEmployee(
  employeePublicKey: string
): Promise<AvailableVcnCard[]> {
  const availableCards: AvailableVcnCard[] = []

  // Fetch Marqeta cards
  try {
    const marqetaResponse = await epsClient.get<MarqetaFamilyVcnResponse>(
      `/core/finances/family/${encodeURIComponent(employeePublicKey)}/virtual-card`
    )
    const cards = (marqetaResponse.data?.cards ?? []).map(
      remapFamilyVirtualCardCard
    )
    availableCards.push(
      ...cards.map((card) => ({
        id: card.card_public_key ?? '',
        cardToken: card.token,
        card: card.pan ?? '',
        HIOS: card.metadata?.HiosId ?? '',
        name: `${card.firstName ?? ''} ${card.lastName ?? ''}`.trim(),
        userPublicKey: card.user_public_key ?? '',
        status: card.state ?? '',
        virtualCardType: VirtualCardType.MARQETA,
      }))
    )
  } catch (e) {
    console.warn('Could not load related Marqeta virtual cards:', e)
  }

  // Fetch WEX cards
  try {
    const wexCards = await listWexVirtualCardsByEmployeeId(employeePublicKey)
    // Fetch HIOS for each WEX card (from application)
    const applicationIds = wexCards.map(
      (card) => card.rawData?.userDefinedFields?.benefitId
    )
    const healthPlanIdByApplicationId = await fetchHealthPlanIdByApplicationId(
      applicationIds.filter(Boolean) as string[]
    )
    availableCards.push(
      ...wexCards.map((card) => {
        const applicationId = card.rawData?.userDefinedFields?.benefitId
        const HIOS = applicationId
          ? (healthPlanIdByApplicationId.get(applicationId) ?? '')
          : ''
        return {
          id: card.merchantLogId ?? '',
          merchantLogId: card.merchantLogId,
          card: card.rawData?.virtualCard?.number ?? '',
          HIOS,
          name: card.rawData?.userDefinedFields?.fullname ?? '',
          userPublicKey: employeePublicKey,
          status: (card.status ?? '').toUpperCase(),
          virtualCardType: VirtualCardType.WEX,
        }
      })
    )
  } catch (e) {
    console.warn('Could not load related WEX virtual cards:', e)
  }

  return availableCards
}

async function fetchHealthPlanIdByApplicationId(
  applicationPublicKeys: string[]
): Promise<Map<string, string>> {
  const uniqueIds = Array.from(new Set(applicationPublicKeys.filter(Boolean)))
  const entries = await Promise.all(
    uniqueIds.map(async (applicationId) => {
      try {
        const { data } = await epsClient.get<ApplicationDto>(
          `/core/benefits/applications/${encodeURIComponent(applicationId)}`
        )
        return [
          applicationId,
          data?.HealthPlanId ?? data?.healthPlanId ?? '',
        ] as const
      } catch (e) {
        console.warn(`Could not load application ${applicationId} for HIOS:`, e)
        return [applicationId, ''] as const
      }
    })
  )
  return new Map(entries)
}

// ============================================================================
// Marqeta VCN Operations (via EPS /core/finances/...)
// ============================================================================

/** Create a new Marqeta virtual card for a benefit. */
export async function createMarqetaVirtualCard(
  benefitId: string
): Promise<MarqetaVirtualCardResponse> {
  const response = await financeService({
    route: `benefit/${benefitId}/virtual-card`,
    method: 'POST',
  })
  return response as MarqetaVirtualCardResponse
}

/** Reuse an existing Marqeta virtual card for a benefit. */
export async function reuseMarqetaVirtualCard(params: {
  benefitId: string
  cardId: string
  cardPublicKey: string
  userPublicKey: string
}): Promise<MarqetaVirtualCardResponse> {
  const response = await financeService({
    route: `benefit/${params.benefitId}/virtual-card/reuse`,
    method: 'POST',
    body: {
      VirtualCardId: params.cardId,
      VirtualCardPublicKey: params.cardPublicKey,
      UserPublicKey: params.userPublicKey,
    },
  })
  return response as MarqetaVirtualCardResponse
}

// ============================================================================
// WEX VCN with ShowPan (for card details display)
// ============================================================================

/**
 * Create WEX virtual card and fetch showpan details.
 * Returns card with full details including PAN, CVV, expiration.
 */
export async function createWexVirtualCardWithDetails(
  benefitId: string
): Promise<WexVirtualCardWithDetails> {
  const res = await createWexVirtualCard(benefitId)
  // Wait a bit for card to be ready
  await new Promise((resolve) => setTimeout(resolve, 1000))

  try {
    const showpanResult = await getPartnerVcnWexShowPan(res.merchantLogId)
    return mapWexShowPanToVirtualCard(showpanResult, res.status)
  } catch {
    // Fallback to basic card info if showpan fails
    return {
      id: res.publicKey,
      merchantLogId: res.merchantLogId,
      state: res.status,
      vcnProvider: VirtualCardType.WEX,
    }
  }
}

/**
 * Perform WEX card action (suspend/terminate/reactivate) and return updated card details.
 */
export async function performWexCardAction(
  action: 'suspend' | 'terminate' | 'reactivate',
  merchantLogId: string,
  benefitId?: string
): Promise<WexVirtualCardWithDetails> {
  let res: { merchantLogId: string; status: string }

  switch (action) {
    case 'suspend':
      res = await suspendWexVirtualCard(merchantLogId)
      break
    case 'terminate':
      res = await terminateWexVirtualCard(merchantLogId, benefitId)
      break
    case 'reactivate':
      res = await activateWexVirtualCard(merchantLogId)
      break
    default:
      throw new Error(`Unsupported WEX VCN action: ${action}`)
  }

  const showpanResult = await getPartnerVcnWexShowPan(res.merchantLogId)
  return mapWexShowPanToVirtualCard(showpanResult, res.status)
}

/**
 * Reuse WEX virtual card and return updated card details.
 */
export async function reuseWexVirtualCardWithDetails(
  merchantLogId: string,
  benefitId: string
): Promise<WexVirtualCardWithDetails> {
  const res = await reuseWexVirtualCard(merchantLogId, benefitId)
  const showpanResult = await getPartnerVcnWexShowPan(res.merchantLogId)
  return mapWexShowPanToVirtualCard(showpanResult, res.status)
}

// ============================================================================
// Marqeta VCN Card Actions (via VCN Service)
// ============================================================================

const ENROLLME_SOURCE_SYSTEM = 3
const CALLER = 'admin-web-client'

function getVcnServiceBasePath(): string {
  if (isStg()) {
    return STG_ENV.vcnServiceUrl
  }
  if (isDevelopment()) {
    return 'https://vcn-service.dev.surecolabs.com'
  }
  return 'https://vcn-service.docday.com'
}

/**
 * Perform a Marqeta card action (suspend, terminate, reactivate) via VCN Service.
 * `POST /card/{action}` on vcn-service.docday.com
 */
export async function performMarqetaCardAction(
  params: PerformMarqetaCardActionParams
): Promise<void> {
  const {
    action,
    cardPublicKey,
    reason = 'User Action',
    sourceUserId,
    benefitId,
  } = params
  const basePath = getVcnServiceBasePath()

  await axios({
    baseURL: basePath,
    method: 'post',
    url: `/card/${action}`,
    data: {
      card_public_key: cardPublicKey,
      reason,
      source_user_id: sourceUserId,
      caller_name: CALLER,
      source_system_id: ENROLLME_SOURCE_SYSTEM,
    },
    params: action === 'terminate' && benefitId ? { benefitId } : undefined,
  })
}

function mapWexShowPanToVirtualCard(
  res: Awaited<ReturnType<typeof getPartnerVcnWexShowPan>>,
  epsVcnStatus?: string
): WexVirtualCardWithDetails {
  const vc = res.virtualCard
  const payment = res.virtualCardPayment
  const udf = res.userDefinedFields

  return {
    id: res.id,
    merchantLogId: res.id, // WEX uses the same ID as merchantLogId
    pan: vc?.number,
    cvv_number: vc?.securityCode,
    expiration_time: vc?.expiration,
    cardholderName: udf?.fullname ?? undefined,
    monthly_card_limit: payment?.creditLimitPerMonth ?? payment?.creditLimit,
    state: epsVcnStatus ?? res.paymentStatus,
    created_time: res.createdDate,
    last_modified_time: res.modifiedDate,
    vcnProvider: VirtualCardType.WEX,
  }
}
